import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

export default function LanguageSwitcher() {
    const { i18n, t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const current =
        SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ||
        SUPPORTED_LANGUAGES[0];

    const choose = (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-2.5 border border-neutral-200 hover:border-[#0089c8] hover:text-black text-xs font-semibold uppercase tracking-[0.18em] rounded-lg transition-colors"
                aria-label={t("header.language")}
                aria-expanded={open}
                data-testid="language-switcher-btn"
            >
                <Globe className="w-4 h-4" />
                <span>{current.short}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-44 bg-white border border-neutral-200 rounded-lg shadow-2xl overflow-hidden z-50"
                    data-testid="language-switcher-menu"
                >
                    {SUPPORTED_LANGUAGES.map((l) => {
                        const active = l.code === current.code;
                        return (
                            <button
                                key={l.code}
                                type="button"
                                onClick={() => choose(l.code)}
                                className={`w-full text-left px-4 py-2.5 flex items-center justify-between text-sm transition-colors ${
                                    active
                                        ? "bg-neutral-100 text-neutral-900"
                                        : "text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100/5"
                                }`}
                                data-testid={`language-option-${l.code}`}
                            >
                                <span className="flex items-center gap-3">
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                                        {l.short}
                                    </span>
                                    <span>{l.label}</span>
                                </span>
                                {active && <Check className="w-4 h-4" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
